import MBDialog from "components/MBDialog";
import { Divider, Grid, Typography, Card, Stack } from "@mui/material";
import SoftBox from "components/SoftBox";
import PropTypes from "prop-types";
import MBCopyText from "components/MBCopyText";
import React, { useEffect, useState, useMemo } from "react";
import useAPI from "hooks/useAPI";
import PopupLinearProgress from "components/MBLinearProgress/PopupLinearProgress";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import WithPermission from "components/WithPermission";
import SoftTypography from "components/SoftTypography";
import moment from "moment";
import useConstants from "constants";
import DataTable from "react-data-table-component";
import Loader from "components/loader";
import SoftInput from "components/SoftInput";
import { t } from "i18next";
import useAlert from "hooks/useAlert";
import AlertMessage from "components/UI/AlertMessage";

const LogDetails = ({ openModel, closeModel, title, rowId }) => {
  const { sql_date_format, per_page } = useConstants();
  const [dataLoading, setDataLoading] = useState(false);
  const { api } = useAPI();
  const [logData, setLogData] = useState({});
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(per_page);
  const [search, setSearch] = useState("");
  const [sortColumn, setSortColumn] = useState("field");
  const [sortDirection, setSortDirection] = useState("asc");
  const [permissions, setPermissions] = useState([]);
  const [isPermissionField, setIsPermissionField] = useState(false);
  const { errorState, showAlert } = useAlert();

  const resetData = () => {
    setLogData({});
    setData([]);
    setTotalRows(0);
    setPage(1);
    setPerPage(per_page);
    setSearch("");
    setSortColumn("field");
    setSortDirection("asc");
    setIsPermissionField(false);
  };
  const labels = {
    group_id: "Role Id",
    group_name: "Role Name",
  };

  const fetchDetails = async () => {
    setDataLoading(true);
    try {
      const response = await api.get(`/activity-log/${rowId}`);
      if (response.status === 200 || response.status === 201) {
        setLogData(response?.data?.log_data);
        if (response?.data?.log_data?.data?.some((item) => item.field === "permissions")) {
          setIsPermissionField(true);
        }
        setData(response?.data?.log_data?.data || []);
        setTotalRows(response?.data?.log_data?.data?.length || 0);
      }
    } catch (error) {
      console.error("Error fetching log details:", error);
      resetData();
    } finally {
      setDataLoading(false);
    }
  };

  useEffect(() => {
    if (rowId != null) {
      fetchDetails();
    }
  }, [rowId]);

  // Client-side filtering
  const filteredData = useMemo(() => {
    if (!search) return data;
    return data.filter((item) => {
      if (item.field === "permissions") {
        const oldPermissions = Array.isArray(item.old) ? item.old.join(", ") : item.old;
        const newPermissions = Array.isArray(item.new) ? item.new.join(", ") : item.new;

        return (
          String(item.field).toLowerCase().includes(search.toLowerCase()) ||
          String(oldPermissions).toLowerCase().includes(search.toLowerCase()) ||
          String(newPermissions).toLowerCase().includes(search.toLowerCase())
        );
      }

      // Normal handling for other fields
      return Object.values(item).some((val) =>
        String(val).toLowerCase().includes(search.toLowerCase())
      );
    });
  }, [data, search]);

  // Client-side sorting
  const sortedData = useMemo(() => {
    if (!sortColumn) return filteredData;

    return [...filteredData].sort((a, b) => {
      const aValue = a[sortColumn];
      const bValue = b[sortColumn];

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  }, [filteredData, sortColumn, sortDirection]);

  // Client-side pagination
  const paginatedData = useMemo(() => {
    const startIndex = (page - 1) * perPage;
    return sortedData.slice(startIndex, startIndex + perPage);
  }, [sortedData, page, perPage]);

  // Update total rows when filtered data changes
  useEffect(() => {
    setTotalRows(filteredData.length);
  }, [filteredData]);

  const handleSort = (column, direction) => {
    setSortColumn(column.columnKey);
    setSortDirection(direction);
  };

  const handlePageChange = (newPage) => setPage(newPage);

  const handlePerPageChange = (newPerPage) => {
    setPerPage(newPerPage);
    setPage(1);
  };
  const handleSearchChange = (event) => {
    setSearch(event.target.value);
  };
  const tableHeading = (heading) => {
    return <span style={{ textTransform: "uppercase" }}>{heading}</span>;
  };

  const columns = [
    {
      name: tableHeading(t("lang.field")),
      columnKey: "field",
      selector: (row) => {
        const field = String(row.field);
        if (labels.hasOwnProperty(field)) {
          return labels[field];
        }
        return field.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
      },
      sortable: true,
      width: isPermissionField ? "20%" : "30%",
    },
    {
      name: tableHeading(t("lang.old_value")),
      columnKey: "old",
      selector: (row) => {
        if (row.field === "permissions" && Array.isArray(row.old)) {
          return row.old.join(", ");
        }
        return row.old;
      },
      sortable: true,
      width: isPermissionField ? "40%" : "35%",
      wrap: true,
    },
    {
      name: tableHeading(t("lang.new_value")),
      columnKey: "new",
      selector: (row) => {
        if (row.field === "permissions" && Array.isArray(row.new)) {
          return row.new.join(", ");
        }
        return row.new;
      },
      sortable: true,
      width: isPermissionField ? "40%" : "35%",
      wrap: true,
    },
  ];

  return (
    <MBDialog
      openModel={openModel}
      closeModel={closeModel}
      title={title}
      actionBtns={true}
      fullWidth
      maxWidth="lg"
    >
      {dataLoading ? (
        <Grid container spacing={1}>
          <Grid item xs={12} overflow={"hidden"}>
            <PopupLinearProgress />
          </Grid>
        </Grid>
      ) : (
        <>
          <Divider />
          <SoftBox mt={3} sx={{ position: "relative" }}>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <SoftBox mt={2}>
                  <Card
                    sx={{
                      boxShadow:
                        "0rem 0.25rem 0.375rem -0.0625rem rgba(0, 0, 0, 0.1), 0rem -0.125rem 0.25rem -0.0625rem rgba(0, 51, 235, 0.06)",
                    }}
                  >
                    <SoftBox pt={1} px={3} display="flex" gap={2} alignItems="center">
                      <SoftTypography
                        variant="body2"
                        sx={{ textTransform: "capitalize" }}
                        fontWeight="bold"
                      >
                        {t("lang.source")} :
                      </SoftTypography>
                      <SoftTypography
                        variant="body2"
                        fontWeight="regular"
                        sx={{ textTransform: "capitalize" }}
                      >
                        {logData.source_name}
                      </SoftTypography>
                    </SoftBox>
                    <Divider />
                    <SoftBox px={3} pt={1}>
                      <Grid container spacing={2}>
                        {/* first col */}
                        <Grid item xs={12} md={6}>
                          <SoftBox mb={1}>
                            <Grid container spacing={0}>
                              <Grid item xs={12} md={4}>
                                <SoftTypography
                                  variant="button"
                                  fontWeight="bold"
                                  sx={{ textTransform: "capitalize" }}
                                >
                                  {t("lang.created_on")}
                                </SoftTypography>
                              </Grid>
                              <Grid item xs={12} md={8}>
                                <SoftTypography
                                  variant="button"
                                  fontWeight="regular"
                                  sx={{ textTransform: "capitalize" }}
                                >
                                  {logData.action_datetime
                                    ? moment(logData.action_datetime).format(sql_date_format)
                                    : null}
                                </SoftTypography>
                              </Grid>
                            </Grid>
                          </SoftBox>
                          {(logData.user_id || logData.channel_id) && (
                            <SoftBox mb={1}>
                              <Grid container spacing={0}>
                                <Grid item xs={12} md={4}>
                                  <SoftTypography
                                    variant="button"
                                    fontWeight="bold"
                                    sx={{ textTransform: "capitalize" }}
                                  >
                                    {logData.source_type === 1
                                      ? t("lang.user_id")
                                      : logData.source_type === 2
                                      ? t("lang.channel_id")
                                      : logData.source_type === 3 && logData.user_id
                                      ? t("lang.user_id")
                                      : t("lang.channel_id")}
                                  </SoftTypography>
                                </Grid>
                                <Grid item xs={12} md={8}>
                                  <SoftTypography
                                    variant="button"
                                    fontWeight="regular"
                                    sx={{ textTransform: "capitalize" }}
                                  >
                                    {logData.source_type === 1
                                      ? logData.user_id
                                      : logData.source_type === 2
                                      ? logData.channel_id
                                      : logData.source_type === 3 && logData.user_name
                                      ? logData.user_id
                                      : logData.channel_id}
                                  </SoftTypography>
                                </Grid>
                              </Grid>
                            </SoftBox>
                          )}
                          <SoftBox mb={1}>
                            <Grid container spacing={0}>
                              <Grid item xs={12} md={4}>
                                <SoftTypography
                                  variant="button"
                                  fontWeight="bold"
                                  sx={{ textTransform: "capitalize" }}
                                >
                                  {t("lang.ip_address")}
                                </SoftTypography>
                              </Grid>
                              <Grid item xs={12} md={8}>
                                <SoftTypography
                                  variant="button"
                                  fontWeight="regular"
                                  sx={{ textTransform: "capitalize" }}
                                >
                                  {logData.ip_address}
                                </SoftTypography>
                              </Grid>
                            </Grid>
                          </SoftBox>
                        </Grid>
                        {/* second col */}
                        <Grid item xs={12} md={6}>
                          <SoftBox mb={1}>
                            <Grid container spacing={0}>
                              <Grid item xs={12} md={4}>
                                <SoftTypography
                                  variant="button"
                                  fontWeight="bold"
                                  sx={{ textTransform: "capitalize" }}
                                >
                                  {t("lang.action_type")}
                                </SoftTypography>
                              </Grid>
                              <Grid item xs={12} md={8}>
                                <SoftTypography
                                  variant="button"
                                  fontWeight="regular"
                                  sx={{ textTransform: "capitalize" }}
                                >
                                  {logData.action_type}
                                </SoftTypography>
                              </Grid>
                            </Grid>
                          </SoftBox>
                          {(logData.user_name || logData.channel_name) && (
                            <SoftBox mb={1}>
                              <Grid container spacing={0}>
                                <Grid item xs={12} md={4}>
                                  <SoftTypography
                                    variant="button"
                                    fontWeight="bold"
                                    sx={{ textTransform: "capitalize" }}
                                  >
                                    {logData.source_type === 1
                                      ? t("lang.user_name")
                                      : logData.source_type === 2
                                      ? t("lang.channel_name")
                                      : logData.source_type === 3 && logData.user_name
                                      ? t("lang.user_name")
                                      : t("lang.channel_name")}
                                  </SoftTypography>
                                </Grid>
                                <Grid item xs={12} md={8}>
                                  <SoftTypography
                                    variant="button"
                                    fontWeight="regular"
                                    sx={{ textTransform: "capitalize" }}
                                  >
                                    {logData.source_type === 1
                                      ? logData.user_name
                                      : logData.source_type === 2
                                      ? logData.channel_name
                                      : logData.source_type === 3 && logData.user_name
                                      ? logData.user_name
                                      : logData.channel_name}
                                  </SoftTypography>
                                </Grid>
                              </Grid>
                            </SoftBox>
                          )}

                          <SoftBox mb={1}>
                            <Grid container spacing={0}>
                              <Grid item xs={12} md={4}>
                                <SoftTypography
                                  variant="button"
                                  fontWeight="bold"
                                  sx={{ textTransform: "capitalize" }}
                                >
                                  {t("lang.operation_system")}
                                </SoftTypography>
                              </Grid>
                              <Grid item xs={12} md={8}>
                                <SoftTypography
                                  variant="button"
                                  fontWeight="regular"
                                  sx={{ textTransform: "capitalize" }}
                                >
                                  {logData.os}
                                </SoftTypography>
                              </Grid>
                            </Grid>
                          </SoftBox>
                        </Grid>
                      </Grid>
                    </SoftBox>
                  </Card>
                </SoftBox>
                <SoftBox mt={3}>
                  <Card
                    sx={{
                      boxShadow:
                        "0rem 0.25rem 0.375rem -0.0625rem rgba(0, 0, 0, 0.1), 0rem -0.125rem 0.25rem -0.0625rem rgba(0, 51, 235, 0.06)",
                    }}
                  >
                    <SoftBox p={3}>
                      <Stack spacing={12} direction="row">
                        <SoftTypography variant="button" fontWeight="bold">
                          {t("lang.description")}
                        </SoftTypography>
                        <SoftTypography variant="button" fontWeight="regular">
                          {logData.description}
                        </SoftTypography>
                      </Stack>
                    </SoftBox>
                  </Card>
                </SoftBox>
                {logData.action_type === "Update" && logData.data && (
                  <SoftBox mt={3}>
                    <Card
                      sx={{
                        boxShadow:
                          "0rem 0.25rem 0.375rem -0.0625rem rgba(0, 0, 0, 0.1), 0rem -0.125rem 0.25rem -0.0625rem rgba(0, 51, 235, 0.06)",
                      }}
                    >
                      <SoftBox
                        px={3}
                        pt={2}
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <SoftTypography fontWeight="bold">
                          {t("lang.fields_updated")}
                        </SoftTypography>

                        <SoftBox width="12rem">
                          <SoftInput
                            placeholder="Search..."
                            size="small"
                            fullWidth
                            value={search}
                            onChange={handleSearchChange}
                          />
                        </SoftBox>
                      </SoftBox>

                      <SoftBox px={3} pt={1}>
                        <DataTable
                          columns={columns}
                          data={paginatedData}
                          progressPending={loading}
                          progressComponent={<Loader />}
                          pagination
                          paginationServer
                          paginationTotalRows={totalRows}
                          onChangeRowsPerPage={handlePerPageChange}
                          onChangePage={handlePageChange}
                          paginationRowsPerPageOptions={[5, 10, 25, 50, 100]}
                          onSort={handleSort}
                          sortServer={false}
                        />
                      </SoftBox>
                    </Card>
                  </SoftBox>
                )}
              </Grid>
            </Grid>
          </SoftBox>
        </>
      )}
      <AlertMessage {...errorState} />
    </MBDialog>
  );
};

LogDetails.propTypes = {
  openModel: PropTypes.bool.isRequired,
  closeModel: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  rowId: PropTypes.number,
};

export default LogDetails;
